<template>
  <!--
    WB-M1A -- Borna's own board toolbar, outside the editor (doc 25 §2.6). It
    only reports what was pressed; the Workspace drives the editor.

    The M1A tool set (doc 23 §13.4): select/move, pen, eraser, text, rectangle,
    ellipse, line, arrow, undo/redo, reset view. No image, no equation and no
    send control exist in M1A.

    WB-M1B S4 (doc 23 §23): one more control, ƒx, after the text tool. It is
    not an editor tool -- it is never the active tool -- it asks the Workspace
    to open the Equation editor.

    WB-M1B S6 (doc 23 §29): the row scrolls sideways when the board is
    narrower than it (a phone). Undo and redo do not scroll away with it:
    they stay at the end of what is visible. The controls and their order are
    unchanged.

    WB-M2A S2 (doc 31 §9): one more control, «از AI Tutor بپرس», OUTSIDE
    the scrolling row at the toolbar's end, so it is always in reach. It is
    not an editor tool: it asks the Workspace to open the share sheet. It
    exists only when the Workspace offers it (`share`); without it the
    toolbar is byte for byte the WB-M1B toolbar.

    P41 BT-53: the pen settings -- the prototype's «رنگ قلم» swatch, last
    before undo / redo and held with them (so it is always in reach on a
    phone). It opens a small panel with the prototype's four colours and
    three widths (the widths are the owner-approved addition, DEV-1). The
    Workspace holds the setting (`penStyle`); this only reports a choice.

    P41 BT-54: «پاک کردن همهٔ تخته», after reset view. It asks first, in a
    small panel here (never the browser's own confirm), and says that undo
    brings the board back only while it stays open; only a confirmed press
    reaches the Workspace (`clear`).

    Both panels open under the toolbar, outside the scrolling row (which
    would clip them); Escape, a press elsewhere, or the same control closes
    them.

    P43-01 (Owner UAT, supersedes the P42-01 order): Selection is the FIRST
    tool and the pen (freedraw) the second, so both are in view without
    scrolling. P42-01 (owner decisions): L-1 is gone by construction: the
    tools scroll in their own row, and the controls that must always be in
    reach -- pen settings, undo / redo, reset view, clear and Send -- are real
    layout space beside / under it, never an overlay on a tool (the old
    sticky group sat on the first four tools of a phone). Below 768px the
    toolbar is two lines: the tools, then those controls with Send.

    P42-02 (owner decisions): the end control is «ارسال به برنا» with a
    paper-plane icon on a solid background; on a phone it keeps its place in
    the toolbar with the icon and the short label «ارسال». It still only asks
    the Workspace to open the share dialog (`share`).
  -->
  <div
    class="ai-tutor-board-toolbar"
    :class="{
      'ai-tutor-board-toolbar--rail-end':
        railOn('scroll') || railOn('controls'),
      'ai-tutor-board-toolbar--rail-row': railOn('row'),
    }"
    role="toolbar"
    :aria-label="$t('aiTutorBoard.toolbar.label')"
    data-testid="ai-tutor-board-toolbar"
  >
    <!-- P42 (owner decision 2): the tools and the pen / undo / redo / reset /
         clear controls share ONE horizontally scrolling region; «ارسال به
         برنا» sits outside it, so it is never scrolled away and never alone
         on a line of its own. On a phone this wrapper dissolves (see the
         mobile rules): the tools scroll on line 1, the controls and Send
         share line 2. -->
    <div
      ref="scroll"
      class="ai-tutor-board-toolbar__scroll"
      :class="fade('scroll')"
      data-testid="ai-tutor-board-toolbar-scroll"
      @scroll.passive="onRowScroll"
      @focusin="onFocusIn"
      @wheel="onWheel"
    >
      <div
        ref="row"
        class="ai-tutor-board-toolbar__row"
        :class="fade('row')"
        data-testid="ai-tutor-board-toolbar-row"
        @scroll.passive="onRowScroll"
      >
        <template v-for="(group, index) in groups">
          <span
            v-if="index > 0"
            :key="`separator-${index}`"
            class="ai-tutor-board-toolbar__separator"
            aria-hidden="true"
          ></span>
          <button
            v-for="tool in group"
            :key="tool"
            type="button"
            class="ai-tutor-board-toolbar__tool"
            :class="{
              'ai-tutor-board-toolbar__tool--active': tool === activeTool,
            }"
            :data-testid="`ai-tutor-board-tool-${tool}`"
            :disabled="disabled"
            :aria-label="$t(`aiTutorBoard.toolbar.tools.${tool}`)"
            :aria-pressed="tool === activeTool ? 'true' : 'false'"
            :title="$t(`aiTutorBoard.toolbar.tools.${tool}`)"
            @click="$emit('tool', tool)"
          >
            <board-icon :name="iconFor(tool)" :size="20" />
          </button>
          <button
            v-if="index === 0"
            :key="`equation-${index}`"
            type="button"
            class="ai-tutor-board-toolbar__tool ai-tutor-board-toolbar__tool--fx"
            data-testid="ai-tutor-board-equation"
            :disabled="disabled"
            :aria-label="$t('aiTutorBoard.toolbar.tools.equation')"
            aria-haspopup="dialog"
            :title="$t('aiTutorBoard.toolbar.tools.equation')"
            @click="$emit('equation')"
          >
            <span aria-hidden="true">ƒx</span>
          </button>
        </template>
      </div>
      <span
        class="ai-tutor-board-toolbar__separator ai-tutor-board-toolbar__separator--between"
        aria-hidden="true"
      ></span>
      <div
        ref="controls"
        class="ai-tutor-board-toolbar__controls"
        :class="fade('controls')"
        data-testid="ai-tutor-board-toolbar-controls"
        @scroll.passive="onRowScroll"
      >
        <div
          class="ai-tutor-board-toolbar__history"
          data-testid="ai-tutor-board-toolbar-history"
        >
          <button
            ref="penButton"
            type="button"
            class="ai-tutor-board-toolbar__tool ai-tutor-board-toolbar__tool--pen"
            :class="{ 'ai-tutor-board-toolbar__tool--open': open === 'pen' }"
            data-testid="ai-tutor-board-pen"
            :data-pen-color="pen.color"
            :data-pen-width="String(pen.width)"
            :disabled="disabled"
            :aria-label="penLabel"
            :title="penLabel"
            aria-haspopup="dialog"
            :aria-expanded="open === 'pen' ? 'true' : 'false'"
            @click="toggle('pen')"
          >
            <span
              class="ai-tutor-board-toolbar__swatch"
              :style="{ backgroundColor: pen.color }"
              aria-hidden="true"
            ></span>
            <span
              class="ai-tutor-board-toolbar__swatch-width"
              :style="{
                backgroundColor: pen.color,
                height: `${widthMark(pen.width)}px`,
              }"
              aria-hidden="true"
            ></span>
          </button>
          <button
            v-for="action in actions"
            :key="action"
            type="button"
            class="ai-tutor-board-toolbar__tool"
            :data-testid="`ai-tutor-board-${action}`"
            :disabled="disabled"
            :aria-label="$t(`aiTutorBoard.toolbar.tools.${action}`)"
            :title="$t(`aiTutorBoard.toolbar.tools.${action}`)"
            @click="$emit(action)"
          >
            <board-icon :name="action" :size="20" />
          </button>
        </div>
        <span
          class="ai-tutor-board-toolbar__separator"
          aria-hidden="true"
        ></span>
        <button
          type="button"
          class="ai-tutor-board-toolbar__tool"
          data-testid="ai-tutor-board-reset-view"
          :disabled="viewDisabled"
          :aria-label="$t('aiTutorBoard.toolbar.tools.resetView')"
          :title="$t('aiTutorBoard.toolbar.tools.resetView')"
          @click="$emit('reset-view')"
        >
          <board-icon name="resetView" :size="20" />
        </button>
        <span
          class="ai-tutor-board-toolbar__separator"
          aria-hidden="true"
        ></span>
        <button
          ref="clearButton"
          type="button"
          class="ai-tutor-board-toolbar__tool ai-tutor-board-toolbar__tool--clear"
          :class="{ 'ai-tutor-board-toolbar__tool--open': open === 'clear' }"
          data-testid="ai-tutor-board-clear"
          :disabled="disabled || clearDisabled"
          :aria-label="$t('aiTutorBoard.toolbar.tools.clear')"
          :title="$t('aiTutorBoard.toolbar.tools.clear')"
          aria-haspopup="dialog"
          :aria-expanded="open === 'clear' ? 'true' : 'false'"
          @click="toggle('clear')"
        >
          <board-icon name="clear" :size="20" />
        </button>
      </div>
    </div>
    <!-- P43-01 (Owner UAT): the scroll rail. Where a region scrolls, a slim
         track with a thumb (how much is in view, and where) sits in its own
         band UNDER the region, so it is never over a tool; it takes no press
         (pointer-events: none) and is hidden from assistive technology (the
         region itself is the control). -->
    <template v-for="key in railKeys">
      <span
        v-if="railOn(key)"
        :key="`rail-${key}`"
        class="ai-tutor-board-toolbar__rail"
        :style="rails[key].track"
        :data-testid="`ai-tutor-board-toolbar-rail-${key}`"
        aria-hidden="true"
      >
        <i
          class="ai-tutor-board-toolbar__rail-thumb"
          :style="rails[key].thumb"
        ></i>
      </span>
    </template>
    <!-- P41 BT-53: the pen panel. -->
    <div
      v-if="open === 'pen'"
      ref="popover"
      class="ai-tutor-board-toolbar__popover"
      role="dialog"
      :aria-label="$t('aiTutorBoard.pen.title')"
      data-testid="ai-tutor-board-pen-panel"
      :style="popoverStyle"
      @keydown.esc.stop.prevent="close(true)"
      @focusout="onFocusOut"
    >
      <p class="ai-tutor-board-toolbar__popover-label">
        {{ $t('aiTutorBoard.pen.colorLabel') }}
      </p>
      <div
        class="ai-tutor-board-toolbar__colors"
        role="group"
        :aria-label="$t('aiTutorBoard.pen.colorLabel')"
      >
        <button
          v-for="color in penColors"
          :key="color.key"
          type="button"
          class="ai-tutor-board-toolbar__color"
          :class="{
            'ai-tutor-board-toolbar__color--chosen': color.value === pen.color,
          }"
          :data-testid="`ai-tutor-board-pen-color-${color.key}`"
          :aria-label="$t(`aiTutorBoard.pen.colors.${color.key}`)"
          :title="$t(`aiTutorBoard.pen.colors.${color.key}`)"
          :aria-pressed="color.value === pen.color ? 'true' : 'false'"
          @click="choose({ color: color.value })"
        >
          <span
            class="ai-tutor-board-toolbar__color-dot"
            :style="{ backgroundColor: color.value }"
            aria-hidden="true"
          ></span>
        </button>
      </div>
      <p class="ai-tutor-board-toolbar__popover-label">
        {{ $t('aiTutorBoard.pen.widthLabel') }}
      </p>
      <div
        class="ai-tutor-board-toolbar__widths"
        role="group"
        :aria-label="$t('aiTutorBoard.pen.widthLabel')"
      >
        <button
          v-for="width in penWidths"
          :key="width.key"
          type="button"
          class="ai-tutor-board-toolbar__width"
          :class="{
            'ai-tutor-board-toolbar__width--chosen': width.value === pen.width,
          }"
          :data-testid="`ai-tutor-board-pen-width-${width.key}`"
          :aria-pressed="width.value === pen.width ? 'true' : 'false'"
          @click="choose({ width: width.value })"
        >
          <span
            class="ai-tutor-board-toolbar__width-line"
            :style="{
              backgroundColor: pen.color,
              height: `${widthMark(width.value)}px`,
            }"
            aria-hidden="true"
          ></span>
          <span>{{ $t(`aiTutorBoard.pen.widths.${width.key}`) }}</span>
        </button>
      </div>
    </div>
    <!-- P41 BT-54: the confirmation of «پاک کردن همهٔ تخته». -->
    <div
      v-if="open === 'clear'"
      ref="popover"
      class="ai-tutor-board-toolbar__popover ai-tutor-board-toolbar__popover--clear"
      role="alertdialog"
      aria-labelledby="ai-tutor-board-clear-title"
      aria-describedby="ai-tutor-board-clear-body"
      data-testid="ai-tutor-board-clear-confirm"
      :style="popoverStyle"
      @keydown.esc.stop.prevent="close(true)"
      @focusout="onFocusOut"
    >
      <strong
        id="ai-tutor-board-clear-title"
        class="ai-tutor-board-toolbar__popover-title"
        >{{ $t('aiTutorBoard.clear.title') }}</strong
      >
      <p
        id="ai-tutor-board-clear-body"
        class="ai-tutor-board-toolbar__popover-body"
      >
        {{ $t('aiTutorBoard.clear.body') }}
      </p>
      <div class="ai-tutor-board-toolbar__popover-actions">
        <button
          ref="clearCancel"
          type="button"
          class="ai-tutor-board-toolbar__popover-button"
          data-testid="ai-tutor-board-clear-cancel"
          @click="close(true)"
        >
          {{ $t('aiTutorBoard.clear.cancel') }}
        </button>
        <button
          type="button"
          class="ai-tutor-board-toolbar__popover-button ai-tutor-board-toolbar__popover-button--danger"
          data-testid="ai-tutor-board-clear-yes"
          @click="confirmClear"
        >
          {{ $t('aiTutorBoard.clear.confirm') }}
        </button>
      </div>
    </div>
    <!-- WB-M2A: drawn from a list that is empty unless the Workspace offers
         the action, so that it leaves no node behind. -->
    <div
      v-for="item in shareActions"
      :key="item"
      class="ai-tutor-board-toolbar__share"
      data-testid="ai-tutor-board-toolbar-share"
    >
      <button
        type="button"
        class="ai-tutor-board-toolbar__ask"
        data-testid="ai-tutor-board-share"
        :disabled="shareDisabled"
        :aria-label="$t('aiTutorBoard.share.action')"
        aria-haspopup="dialog"
        :title="$t('aiTutorBoard.share.action')"
        @click="$emit('share')"
      >
        <board-icon name="send" :size="18" />
        <!-- The full wording on a wide board, the short one on a phone; the
             accessible name is always the full one (aria-label above). -->
        <span
          class="ai-tutor-board-toolbar__ask-label ai-tutor-board-toolbar__ask-label--full"
          aria-hidden="true"
          >{{ $t('aiTutorBoard.share.action') }}</span
        >
        <span
          class="ai-tutor-board-toolbar__ask-label ai-tutor-board-toolbar__ask-label--short"
          aria-hidden="true"
          >{{ $t('aiTutorBoard.share.actionShort') }}</span
        >
      </button>
    </div>
  </div>
</template>

<script>
import BoardIcon from './BoardIcon.vue'
import {
  PEN_COLORS,
  PEN_WIDTHS,
  normalizePenStyle,
} from '@/services/aiTutor/boardPen'

// The M1A tools, by their Excalidraw tool type.
// P43-01 (Owner UAT): Selection first, then the pen; the rest keep their order.
export const BOARD_TOOL_GROUPS = [
  ['selection', 'freedraw', 'eraser', 'text'],
  ['rectangle', 'ellipse', 'line', 'arrow'],
]

// P41: the gap kept between a panel and the toolbar's own edges.
const PANEL_MARGIN = 8
// P43-01: the scrolling regions that can carry an overflow cue (template refs),
// a sub-pixel tolerance for "is there more", and a wheel "line" in px.
const CUE_REGIONS = ['scroll', 'row', 'controls']
const CUE_EPSILON = 1
const WHEEL_LINE_PX = 16
// The rail's thumb is never thinner than this share of its track.
const RAIL_MIN_THUMB = 0.14

export default {
  name: 'BoardToolbar',
  components: { BoardIcon },
  props: {
    activeTool: { type: String, default: 'freedraw' },
    // Editing is unavailable: the editor is not up yet, or the board is
    // read-only (a stale tab).
    disabled: { type: Boolean, default: false },
    // Reset view stays usable on a read-only board; only a missing editor
    // disables it.
    viewDisabled: { type: Boolean, default: false },
    // WB-M2A: the Workspace offers «از AI Tutor بپرس».
    share: { type: Boolean, default: false },
    // WB-M2A: nothing can be shared right now (no editor, read-only, an
    // empty board, a share already open).
    shareDisabled: { type: Boolean, default: false },
    // P41 BT-53: the pen settings the Workspace holds ({color, width}).
    penStyle: { type: Object, default: null },
    // P41 BT-54: nothing to clear (an empty board).
    clearDisabled: { type: Boolean, default: false },
  },
  data() {
    return {
      groups: BOARD_TOOL_GROUPS,
      actions: ['undo', 'redo'],
      penColors: PEN_COLORS,
      penWidths: PEN_WIDTHS,
      // P41: the panel that is open -- '' | 'pen' | 'clear' -- and where.
      open: '',
      panelLeft: null,
      // P43-01: for each scrolling region, is there more content beyond its
      // left / right edge? (Resolved from the region's own direction when it is
      // measured, so a right-to-left page fades the right side when it has
      // scrolled and the left side while it has more to show.)
      more: {
        scroll: { left: false, right: false },
        row: { left: false, right: false },
        controls: { left: false, right: false },
      },
      // P43-01 (Owner UAT): the scroll rail of each region that scrolls.
      rails: {
        scroll: { on: false, track: {}, thumb: {} },
        row: { on: false, track: {}, thumb: {} },
        controls: { on: false, track: {}, thumb: {} },
      },
      railKeys: CUE_REGIONS,
    }
  },
  mounted() {
    this.measureCues()
    // The regions change size with the board's width, a font that arrives late
    // and the label of Send: measured again whenever one does.
    if (typeof ResizeObserver !== 'undefined') {
      this.cueObserver = new ResizeObserver(() => this.measureCues())
      for (const key of CUE_REGIONS) {
        const el = this.$refs[key]
        if (el) this.cueObserver.observe(el)
      }
      this.cueObserver.observe(this.$el)
    } else {
      window.addEventListener('resize', this.measureCues)
    }
  },
  computed: {
    shareActions() {
      return this.share ? ['share'] : []
    },
    pen() {
      return normalizePenStyle(this.penStyle)
    },
    // The control says what it is AND what is chosen.
    penLabel() {
      const color = PEN_COLORS.find(entry => entry.value === this.pen.color)
      const width = PEN_WIDTHS.find(entry => entry.value === this.pen.width)
      return this.$t('aiTutorBoard.pen.current', {
        color: this.$t(`aiTutorBoard.pen.colors.${color.key}`),
        width: this.$t(`aiTutorBoard.pen.widths.${width.key}`),
      })
    },
    popoverStyle() {
      return this.panelLeft === null ? {} : { left: `${this.panelLeft}px` }
    },
  },
  watch: {
    // A board that becomes read-only (or loses its editor) closes a panel.
    disabled(now) {
      if (now) this.close(false)
    },
    clearDisabled(now) {
      if (now && this.open === 'clear') this.close(false)
    },
  },
  beforeDestroy() {
    this.unlisten()
    if (this.cueObserver) this.cueObserver.disconnect()
    else window.removeEventListener('resize', this.measureCues)
  },
  methods: {
    // P42-01: the Selection tool wears the selection-area glyph; every other
    // tool wears the glyph of its own name.
    iconFor(tool) {
      return tool === 'selection' ? 'selectArea' : tool
    },
    // The width mark on the controls: thin / medium / thick, readable at
    // the toolbar's size.
    widthMark(width) {
      if (width >= 4) return 5
      if (width >= 2) return 3
      return 1.5
    },
    toggle(kind) {
      if (this.open === kind) {
        this.close(true)
        return
      }
      this.open = kind
      this.panelLeft = null
      this.listen()
      this.$nextTick(() => {
        this.place()
        const first =
          kind === 'clear'
            ? this.$refs.clearCancel
            : this.$refs.popover &&
              this.$refs.popover.querySelector('[aria-pressed="true"]')
        if (first && typeof first.focus === 'function') first.focus()
      })
    },
    close(returnFocus) {
      if (!this.open) return
      const trigger =
        this.open === 'pen' ? this.$refs.penButton : this.$refs.clearButton
      this.open = ''
      this.unlisten()
      if (returnFocus && trigger && typeof trigger.focus === 'function') {
        trigger.focus()
      }
    },
    choose(change) {
      this.$emit('pen-style', normalizePenStyle({ ...this.pen, ...change }))
    },
    // The pressed «پاک کردن» goes away and the clear control is disabled
    // (the board is empty): focus goes to undo, the natural next step
    // (review P3).
    confirmClear() {
      this.close(false)
      this.$emit('clear')
      this.$nextTick(() => {
        const undo =
          this.$el &&
          this.$el.querySelector('[data-testid="ai-tutor-board-undo"]')
        if (undo && typeof undo.focus === 'function') undo.focus()
      })
    },
    // Scrolling the row moves the control: the panel follows it.
    onRowScroll() {
      this.measureCues()
      if (this.open) this.place()
    },
    // P43-01: is there more beyond each edge of a scrolling region? The distance
    // is measured as a magnitude from the region's START edge, so it reads the
    // same in a left-to-right and a right-to-left page (scrollLeft is negative
    // in RTL); the start edge is the right one in RTL. A region that does not
    // scroll (the shared one on a phone, each part on a wide board) has nothing
    // beyond either edge.
    measureCues() {
      for (const key of CUE_REGIONS) {
        const el = this.$refs[key]
        const max = el ? el.scrollWidth - el.clientWidth : 0
        const from = el ? Math.abs(el.scrollLeft) : 0
        const rtl = el ? window.getComputedStyle(el).direction === 'rtl' : false
        const scrolls = max > CUE_EPSILON
        const start = scrolls && from > CUE_EPSILON
        const end = scrolls && max - from > CUE_EPSILON
        const next = { left: rtl ? end : start, right: rtl ? start : end }
        const now = this.more[key]
        if (now.left !== next.left || now.right !== next.right) {
          this.$set(this.more, key, next)
        }
        this.measureRail(key, el, scrolls, max, from, rtl)
      }
    },
    railOn(key) {
      return this.rails[key].on
    },
    // P43-01 (Owner UAT): where the rail sits (under the region, in physical
    // coordinates, so it reads the same in either direction) and how big the
    // thumb is and where. The thumb starts at the region's start edge: the
    // right one in a right-to-left page.
    measureRail(key, el, scrolls, max, from, rtl) {
      const now = this.rails[key]
      if (!scrolls || !this.$el) {
        if (now.on)
          this.$set(this.rails, key, { on: false, track: {}, thumb: {} })
        return
      }
      const root = this.$el.getBoundingClientRect()
      const box = el.getBoundingClientRect()
      const share = Math.min(
        1,
        Math.max(RAIL_MIN_THUMB, el.clientWidth / el.scrollWidth)
      )
      const progress = Math.min(1, Math.max(0, from / max))
      const left = (rtl ? 1 - progress : progress) * (1 - share) * 100
      const next = {
        on: true,
        track: {
          left: `${Math.round(box.left - root.left + 2)}px`,
          width: `${Math.max(0, Math.round(box.width - 4))}px`,
          top: `${Math.round(box.bottom - root.top - 1)}px`,
        },
        thumb: {
          left: `${left.toFixed(2)}%`,
          width: `${(share * 100).toFixed(2)}%`,
        },
      }
      if (JSON.stringify(now) !== JSON.stringify(next)) {
        this.$set(this.rails, key, next)
      }
    },
    // The classes that fade a region's edge (a CSS mask: nothing is added to
    // the layout, and a tap or a click goes straight through to the control).
    fade(key) {
      const more = this.more[key]
      return {
        'ai-tutor-board-toolbar--fade-left': more.left,
        'ai-tutor-board-toolbar--fade-right': more.right,
      }
    },
    // P43-01: on a desktop the shared region has no scrollbar and a mouse wheel
    // only turns vertically, so the tools beyond the edge were out of reach.
    // A vertical wheel over the toolbar moves it sideways; a horizontal wheel
    // or trackpad gesture is the browser's own; at either limit the page keeps
    // the wheel.
    onWheel(event) {
      const el = event.currentTarget
      if (!el || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return
      // Ctrl / Meta + wheel (and a trackpad pinch) is the browser's zoom, Shift
      // + wheel is its own horizontal scroll, and a wheel in pages is not ours
      // to rescale: none of them is taken.
      if (
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.altKey ||
        event.deltaMode === 2
      ) {
        return
      }
      const max = el.scrollWidth - el.clientWidth
      if (max <= CUE_EPSILON) return
      const rtl = window.getComputedStyle(el).direction === 'rtl'
      const step = (event.deltaMode === 1 ? WHEEL_LINE_PX : 1) * event.deltaY
      const from = Math.abs(el.scrollLeft)
      const next = Math.min(max, Math.max(0, from + step))
      if (next === from) return
      event.preventDefault()
      el.scrollLeft = rtl ? -next : next
    },
    // P42: a control reached with the KEYBOARD is brought fully into view in
    // its scrolling region (the browser leaves a partly visible one clipped,
    // and its focus ring with it). Only for keyboard focus: a touch or mouse
    // press must not move the row under the pointer between press and release.
    onFocusIn(event) {
      const target = event && event.target
      if (!target || typeof target.scrollIntoView !== 'function') return
      let keyboard = false
      try {
        keyboard = target.matches(':focus-visible')
      } catch (err) {
        keyboard = false
      }
      if (keyboard)
        target.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    },
    // Tab out of a panel closes it (a press elsewhere already does). Only a
    // focus that lands somewhere else counts: a click that focuses nothing
    // (Safari) leaves `relatedTarget` empty and must not close it.
    onFocusOut(event) {
      const next = event && event.relatedTarget
      if (!next) return
      const panel = this.$refs.popover
      const trigger =
        this.open === 'pen' ? this.$refs.penButton : this.$refs.clearButton
      if (panel && panel.contains(next)) return
      if (trigger && trigger.contains(next)) return
      this.close(false)
    },
    // Under its control, kept inside the toolbar's width. Measured: the
    // control may have been scrolled along the row.
    place() {
      const root = this.$el
      const panel = this.$refs.popover
      const trigger =
        this.open === 'pen' ? this.$refs.penButton : this.$refs.clearButton
      if (!root || !panel || !trigger) return
      const box = root.getBoundingClientRect()
      const anchor = trigger.getBoundingClientRect()
      const width = panel.offsetWidth
      const centre = anchor.left - box.left + anchor.width / 2
      const max = Math.max(PANEL_MARGIN, box.width - width - PANEL_MARGIN)
      this.panelLeft = Math.round(
        Math.min(max, Math.max(PANEL_MARGIN, centre - width / 2))
      )
    },
    listen() {
      if (this.listening) return
      this.listening = true
      document.addEventListener('pointerdown', this.onOutside, true)
      window.addEventListener('resize', this.place)
    },
    unlisten() {
      if (!this.listening) return
      this.listening = false
      document.removeEventListener('pointerdown', this.onOutside, true)
      window.removeEventListener('resize', this.place)
    },
    // A press anywhere but the panel and its own control closes the panel
    // (the control toggles it itself). A press on the BOARD only closes it:
    // it never also starts a stroke (a stray dot with the pen) -- it is
    // stopped here, before the editor sees it.
    onOutside(event) {
      const target = event && event.target
      const panel = this.$refs.popover
      const trigger =
        this.open === 'pen' ? this.$refs.penButton : this.$refs.clearButton
      if (panel && target && panel.contains(target)) return
      if (trigger && target && trigger.contains(target)) return
      if (
        target &&
        typeof target.closest === 'function' &&
        target.closest('.excalidraw')
      ) {
        event.preventDefault()
        event.stopPropagation()
      }
      this.close(false)
    },
  },
}
</script>

<style lang="scss" scoped>
$toolbar-gap: 4px;

.ai-tutor-board-toolbar {
  // P41: the pen / clear panels hang under the toolbar, over the board.
  position: relative;
  z-index: 6;
  display: flex;
  flex: none;
  flex-wrap: nowrap;
  align-items: center;
  gap: 4px $toolbar-gap;
  min-height: 54px;
  padding: 7px 10px;
  border-bottom: 1px solid $borna-border;
  background: $borna-surface;
}

.ai-tutor-board-toolbar__row,
.ai-tutor-board-toolbar__controls,
.ai-tutor-board-toolbar__history {
  display: flex;
  align-items: center;
  gap: $toolbar-gap;
}

// P42 (owner decision 2): ONE scrolling region holds the tools and the
// controls; it takes whatever width «ارسال به برنا» leaves and scrolls when
// that is not enough (a board of ~470-750px), so Send never wraps onto a line
// of its own. Nothing is laid over a tool: the region is real layout space and
// the first tool (Selection) sits at its inline start, in view when the board
// opens. The small padding (cancelled by an equal negative margin, so the
// toolbar is no taller) keeps focus rings from being clipped.
.ai-tutor-board-toolbar__scroll {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  gap: $toolbar-gap;
  min-width: 0;
  padding: 3px 2px;
  margin: -3px -2px;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  // P43-01: a control brought into view (the keyboard, `scrollIntoView`) lands
  // clear of the fade at the edge, so its focus ring is not under it.
  scroll-padding-inline: 28px;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

// P43-01: the overflow cue. A scrolling region that has more beyond an edge
// fades that edge (a CSS mask: no element, no layout, nothing in the way of a
// tap or a click -- the control beneath takes it). The fade is as wide as the
// scroll padding above, so a control brought into view by the keyboard lands
// clear of it, focus ring and all.
.ai-tutor-board-toolbar--fade-left,
.ai-tutor-board-toolbar--fade-right {
  --ai-tutor-board-fade-left: 0px;
  --ai-tutor-board-fade-right: 0px;
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0,
    #000 var(--ai-tutor-board-fade-left),
    #000 calc(100% - var(--ai-tutor-board-fade-right)),
    transparent 100%
  );
  mask-image: linear-gradient(
    to right,
    transparent 0,
    #000 var(--ai-tutor-board-fade-left),
    #000 calc(100% - var(--ai-tutor-board-fade-right)),
    transparent 100%
  );
}

.ai-tutor-board-toolbar--fade-left {
  --ai-tutor-board-fade-left: 28px;
}

.ai-tutor-board-toolbar--fade-right {
  --ai-tutor-board-fade-right: 28px;
}

// P43-01 (Owner UAT): the scroll rail. A slim track + thumb in a band of its
// own under a scrolling region (the toolbar makes room for it below), placed by
// the component in physical coordinates. It covers no control and takes no
// press. A stronger cue than the fade alone, in both directions and at every
// width.
.ai-tutor-board-toolbar__rail {
  position: absolute;
  z-index: 1;
  height: 4px;
  border-radius: 999px;
  background: $borna-indigo-100;
  pointer-events: none;
}

.ai-tutor-board-toolbar__rail-thumb {
  position: absolute;
  top: 0;
  bottom: 0;
  border-radius: 999px;
  background: $borna-indigo-600;
}

// The band for a rail under the last line of the toolbar.
.ai-tutor-board-toolbar--rail-end {
  padding-bottom: 11px;
}

.ai-tutor-board-toolbar__row {
  flex: none;
}

// Pen settings, undo / redo, reset view and clear.
.ai-tutor-board-toolbar__controls {
  flex: none;
}

.ai-tutor-board-toolbar__history {
  flex: none;
}

.ai-tutor-board-toolbar__separator {
  flex: none;
  width: 1px;
  height: 24px;
  margin: 0 5px;
  background: $borna-border;
}

.ai-tutor-board-toolbar__tool {
  display: grid;
  place-items: center;
  flex: none;
  width: 38px;
  height: 38px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 11px;
  background: transparent;
  color: $borna-muted;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: $borna-canvas;
    color: $borna-text;
  }

  &--active,
  &--active:hover {
    border-color: $borna-indigo-300;
    background: $borna-indigo-50;
    color: $borna-indigo-600;
  }

  &:disabled {
    opacity: 0.38;
    cursor: not-allowed;
  }

  // ƒx is a glyph, not a drawn icon: a Latin italic, whatever the page font.
  &--fx {
    font-family: 'Times New Roman', Times, serif;
    font-size: 18px;
    font-style: italic;
    font-weight: 700;
    line-height: 1;
    direction: ltr;
  }

  // P41: a control whose panel is open reads as pressed.
  &--open,
  &--open:hover {
    border-color: $borna-indigo-300;
    background: $borna-indigo-50;
  }

  // P41 BT-53: the prototype's swatch (`.swatch`: 20px, a 2px surface edge
  // and a 1.5px border ring), lifted a little over a bar that shows the
  // width.
  &--pen {
    position: relative;
  }

  &--clear:hover:not(:disabled),
  &--clear:focus-visible {
    color: $borna-error-text;
  }
}

.ai-tutor-board-toolbar__swatch {
  display: block;
  width: 20px;
  height: 20px;
  border: 2px solid $borna-surface;
  border-radius: 50%;
  box-shadow: 0 0 0 1.5px $borna-border;
  transform: translateY(-3px);
}

.ai-tutor-board-toolbar__swatch-width {
  position: absolute;
  bottom: 5px;
  left: 50%;
  width: 16px;
  border-radius: $borna-radius-pill;
  transform: translateX(-50%);
}

// P41: the pen panel and the clear confirmation.
.ai-tutor-board-toolbar__popover {
  position: absolute;
  top: calc(100% + 6px);
  left: $toolbar-gap;
  z-index: 30;
  width: 236px;
  padding: 12px 12px 10px;
  border: 1px solid $borna-border;
  border-radius: $borna-radius-nav;
  background: $borna-surface;
  box-shadow: $borna-shadow-composer;
  color: $borna-text;
  font-size: 11px;
  line-height: 1.75;

  &--clear {
    width: min(300px, calc(100vw - 32px));
  }
}

.ai-tutor-board-toolbar__popover-label {
  margin: 0 0 6px;
  color: $borna-muted;
  font-weight: 700;

  & + .ai-tutor-board-toolbar__colors,
  & + .ai-tutor-board-toolbar__widths {
    margin-bottom: 10px;
  }
}

.ai-tutor-board-toolbar__colors,
.ai-tutor-board-toolbar__widths {
  display: flex;
  gap: 6px;
}

.ai-tutor-board-toolbar__color,
.ai-tutor-board-toolbar__width {
  display: grid;
  place-items: center;
  border: 1px solid transparent;
  border-radius: 11px;
  background: transparent;
  color: $borna-muted;
  font: inherit;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: $borna-canvas;
    color: $borna-text;
  }

  &--chosen,
  &--chosen:hover {
    border-color: $borna-indigo-300;
    background: $borna-indigo-50;
    color: $borna-indigo-600;
  }
}

.ai-tutor-board-toolbar__color {
  width: 42px;
  height: 38px;
}

.ai-tutor-board-toolbar__color-dot {
  display: block;
  width: 22px;
  height: 22px;
  border: 2px solid $borna-surface;
  border-radius: 50%;
  box-shadow: 0 0 0 1.5px $borna-border;
}

.ai-tutor-board-toolbar__width {
  flex: 1 1 0;
  gap: 4px;
  min-height: 46px;
  padding: 6px 4px;
  font-size: 10px;
  font-weight: 700;
}

.ai-tutor-board-toolbar__width-line {
  display: block;
  width: 28px;
  border-radius: $borna-radius-pill;
}

.ai-tutor-board-toolbar__popover-title {
  display: block;
  margin-bottom: 4px;
  color: $borna-ink;
  font-size: 12px;
  font-weight: 800;
}

.ai-tutor-board-toolbar__popover-body {
  margin: 0 0 10px;
  color: $borna-muted;
}

.ai-tutor-board-toolbar__popover-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

.ai-tutor-board-toolbar__popover-button {
  height: 34px;
  padding: 0 12px;
  border: 1px solid $borna-border;
  border-radius: 11px;
  background: $borna-surface;
  color: $borna-text;
  font: inherit;
  font-size: 11px;
  font-weight: 800;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: $borna-canvas;
  }

  &--danger {
    border-color: $borna-error-border;
    background: $borna-error-bg;
    color: $borna-error-text;

    &:hover,
    &:focus-visible {
      border-color: $borna-error-text;
      background: $borna-error-bg;
    }
  }
}

// WB-M2A + P42-02: «ارسال به برنا» -- outside the scrolling row, at the end
// of the toolbar, never scrolled away. The prototype's `.btn-send`: a solid
// indigo button, white copy, a paper-plane icon; pale when it cannot be used.
.ai-tutor-board-toolbar__share {
  display: flex;
  flex: none;
  align-items: center;
  gap: $toolbar-gap;
  margin-inline-start: auto;
}

.ai-tutor-board-toolbar__ask {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 14px;
  border: 1px solid $borna-indigo-600;
  border-radius: 13px;
  background: $borna-indigo-600;
  box-shadow: 0 8px 18px rgba($borna-indigo-600, 0.2);
  color: #fff;
  font-family: inherit;
  font-size: 13px;
  font-weight: 800;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    border-color: $borna-indigo-900;
    background: $borna-indigo-900;
  }

  &:focus-visible {
    outline: 2px solid $borna-indigo-600;
    outline-offset: 2px;
  }

  &:disabled,
  &:disabled:hover {
    border-color: $borna-indigo-100;
    background: $borna-indigo-100;
    box-shadow: none;
    color: $borna-indigo-900;
    opacity: 1;
    cursor: not-allowed;
  }
}

// The full wording on a wide board, the short one on a phone.
.ai-tutor-board-toolbar__ask-label--short {
  display: none;
}

@include borna-mobile {
  // P42-01: on a phone the tools take the first line (and scroll if they
  // must); the pen settings, undo / redo, reset view and clear share the
  // second with Send. The shared scroller dissolves so each part scrolls on
  // its own line; Send stays outside every scroller.
  .ai-tutor-board-toolbar {
    flex-wrap: wrap;
    min-height: 0;
    padding: 6px 8px;
  }

  .ai-tutor-board-toolbar--rail-end {
    padding-bottom: 10px;
  }

  // The tools line has a rail: it sits between the two lines, so the row gives
  // up the small negative margin that cancelled its padding.
  .ai-tutor-board-toolbar--rail-row .ai-tutor-board-toolbar__row {
    margin-bottom: 1px;
  }

  .ai-tutor-board-toolbar__scroll {
    display: contents;
  }

  .ai-tutor-board-toolbar__separator--between {
    display: none;
  }

  .ai-tutor-board-toolbar__row {
    // The margin-box is the full line (the -2px side margins cancel the
    // padding), so the controls still wrap to line 2.
    flex: 1 0 calc(100% + 4px);
    max-width: calc(100% + 4px);
    min-width: 0;
    padding: 3px 2px;
    margin: -3px -2px;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scroll-padding-inline: 28px;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  // Narrower than the controls (320px): they scroll, Send does not.
  .ai-tutor-board-toolbar__controls {
    flex: 1 1 0;
    min-width: 0;
    padding: 3px 2px;
    margin: -3px -2px;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scroll-padding-inline: 28px;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  // Send keeps its place in the toolbar: icon + the short label.
  .ai-tutor-board-toolbar__ask {
    height: 38px;
    padding: 0 12px;
    gap: 6px;
  }

  .ai-tutor-board-toolbar__ask-label--full {
    display: none;
  }

  .ai-tutor-board-toolbar__ask-label--short {
    display: inline;
  }
}
</style>
